import React from 'react';
import styled from 'styled-components';
import { ReactComponent as FailureIcon } from '@elementary/common-ui/assets/close.svg';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/16x16/info.svg';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as SyncArrowsIcon } from '@elementary/common-ui/assets/sync-arrows.svg';
import { ReactComponent as VIcon } from '@elementary/common-ui/assets/v-green.svg';
import { ReactComponent as WarningIcon } from '@elementary/common-ui/assets/warning.svg';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  FlexSpace,
  IconButton,
  Menu,
  SvgIcon,
} from '@elementary/common-ui/components';
import themeBase from '@elementary/common-ui/style/theme';
import { capitalize } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { Tooltip, Typography } from '@mui/material';
import { IntegrationIssue } from 'shared/types';

const STATUS = {
  CONNECTED: 'connected',
  SYNCING: 'syncing',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
};

const STATUS_SEVERITY_ORDER: Record<string, number> = {
  [STATUS.ERROR]: 3,
  [STATUS.WARNING]: 2,
  [STATUS.INFO]: 1,
  [STATUS.SYNCING]: 0,
  [STATUS.CONNECTED]: 0,
};

const STATUS_LABELS: Record<string, string> = {
  [STATUS.INFO]: 'Re-authentication needed',
};

const FailureIconStyled = styled(FailureIcon)`
  * {
    stroke: ${({ theme }) => theme.palette.common.red2};
  }
`;

const statusStylesMap = {
  [STATUS.CONNECTED]: {
    background: themeBase.palette.common.green10,
    text: themeBase.palette.common.green100,
    icon: <VIcon width={18} />,
  },
  [STATUS.SYNCING]: {
    background: themeBase.palette.common.lightBlue5,
    text: themeBase.palette.common.blue7,
    icon: (
      <SvgIcon width={16} fillColor={themeBase.palette.common.blue7}>
        <SyncArrowsIcon />
      </SvgIcon>
    ),
  },
  [STATUS.INFO]: {
    background: themeBase.palette.common.lightBlue5,
    text: themeBase.palette.common.blue7,
    icon: (
      <SvgIcon width={16} fillColor={themeBase.palette.common.blue7}>
        <InfoIcon />
      </SvgIcon>
    ),
  },
  [STATUS.WARNING]: {
    background: themeBase.palette.common.orange10,
    text: themeBase.palette.common.orange100,
    icon: (
      <SvgIcon width={16}>
        <WarningIcon />
      </SvgIcon>
    ),
  },
  [STATUS.ERROR]: {
    background: themeBase.palette.common.red10,
    text: themeBase.palette.common.red2,
    icon: <FailureIconStyled width={18} />,
  },
};

const ItemWrapper = styled(FlexSpace)`
  gap: 8px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.common.gray5};
  padding: 16px;
  border-radius: 8px;
  box-sizing: border-box;
  height: 56px;
`;

const StatusChip = styled(Flex)<{ status: keyof typeof statusStylesMap }>`
  font-size: 14px;
  padding: 4px 8px;
  gap: 4px;
  border-radius: 4px;
  white-space: nowrap;
  background-color: ${({ status }) => statusStylesMap[status].background};
  color: ${({ status }) => statusStylesMap[status].text};
  font-weight: 500;
  width: fit-content;
  align-items: center;
`;

export type IntegrationDetails = {
  id: string;
  icon: React.ReactNode;
  name: string;
  status?: keyof typeof statusStylesMap;
  statusTooltip?: string | null;
  last_synced_at?: string | null;
  usageText?: string;
  customOptions?: { value: string; label: string; color?: string }[];
  issues?: IntegrationIssue[];
};

export type MultipleDestinationsRendererProps = {
  integrations: IntegrationDetails[];
  onChange: (method: string, id: string) => void;
  options: { value: string; label: string; color?: string }[];
};

function computeEffectiveStatus(
  status: keyof typeof statusStylesMap | undefined,
  issues: IntegrationIssue[] | undefined,
): keyof typeof statusStylesMap | undefined {
  const worstIssueSeverity = (issues ?? []).reduce<string | undefined>(
    (worst, issue) => {
      if (
        worst === undefined ||
        (STATUS_SEVERITY_ORDER[issue.severity] ?? 0) >
          (STATUS_SEVERITY_ORDER[worst] ?? 0)
      ) {
        return issue.severity;
      }
      return worst;
    },
    undefined,
  );

  if (worstIssueSeverity === undefined) return status;
  if (status === undefined)
    return worstIssueSeverity as keyof typeof statusStylesMap;

  return (STATUS_SEVERITY_ORDER[worstIssueSeverity] ?? 0) >
    (STATUS_SEVERITY_ORDER[status] ?? 0)
    ? (worstIssueSeverity as keyof typeof statusStylesMap)
    : status;
}

function computeCombinedTooltip(
  statusTooltip: string | null | undefined,
  issues: IntegrationIssue[] | undefined,
): string | null {
  const parts = [
    ...(statusTooltip ? [statusTooltip] : []),
    ...(issues ?? []).map((issue) => issue.description),
  ];
  return parts.length > 0 ? parts.join('\n') : null;
}

const multipleDestinationsRenderer = ({
  integrations,
  onChange,
  options,
}: MultipleDestinationsRendererProps): React.ReactNode => (
  <FlexColumn sx={{ gap: '16px' }}>
    {integrations.map((integration) => {
      const lastSyncedLabel = integration.last_synced_at
        ? `Synced ${localizeDate(integration.last_synced_at).fromNow()}`
        : null;

      const effectiveStatus = computeEffectiveStatus(
        integration.status,
        integration.issues,
      );
      const combinedTooltip = computeCombinedTooltip(
        integration.statusTooltip,
        integration.issues,
      );
      const statusLabel = effectiveStatus
        ? STATUS_LABELS[effectiveStatus] ??
          capitalize(effectiveStatus as string)
        : null;

      return (
        <ItemWrapper key={integration.id}>
          <FlexCenter sx={{ gap: '8px' }}>
            {integration.icon}
            <Typography fontSize="14px">{integration.name}</Typography>
            {integration.usageText && (
              <Typography
                fontSize="12px"
                sx={{ color: 'common.gray2', marginLeft: '8px' }}
              >
                {integration.usageText}
              </Typography>
            )}
          </FlexCenter>

          <Flex sx={{ gap: '12px', alignItems: 'center' }}>
            <Flex sx={{ gap: '8px', alignItems: 'center' }}>
              {lastSyncedLabel && (
                <Typography
                  noWrap
                  sx={{
                    fontSize: '12px',
                    color: 'common.gray2',
                  }}
                >
                  {lastSyncedLabel}
                </Typography>
              )}
              {effectiveStatus &&
                (combinedTooltip ? (
                  <Tooltip title={combinedTooltip} placement="top" arrow>
                    <span tabIndex={0} style={{ display: 'inline-flex' }}>
                      <StatusChip status={effectiveStatus}>
                        {statusStylesMap[effectiveStatus].icon}
                        {statusLabel}
                      </StatusChip>
                    </span>
                  </Tooltip>
                ) : (
                  <StatusChip status={effectiveStatus}>
                    {statusStylesMap[effectiveStatus].icon}
                    {statusLabel}
                  </StatusChip>
                ))}
            </Flex>

            <Menu
              sx={{ cursor: 'pointer', marginLeft: '16px' }}
              closeOnChange
              menuButton={
                <IconButton>
                  <FlexCenter sx={{ height: '14px', width: '14px' }}>
                    <MoreHorizIcon />
                  </FlexCenter>
                </IconButton>
              }
              options={integration.customOptions || options}
              onChange={(value) => onChange(value, integration.id)}
            />
          </Flex>
        </ItemWrapper>
      );
    })}
  </FlexColumn>
);

export default multipleDestinationsRenderer;
